/**
 * The Hiring Manager invite-and-activate landing page.
 *
 * Deliberately not /auth/login: SSO is the primary and only path shown at
 * first (full-width, first, no password field above), because the actual
 * "light" moment this page exists for is zero password creation on that
 * path. A plain-text fallback underneath still gets someone to a password
 * without ever making it the default.
 *
 * The two states below are not visual variants of the same copy -- they are
 * two different facts about the invited email, and saying the wrong one
 * would either promise a fresh signup that silently isn't one, or tell an
 * actual new person they already have an account. `accountExists` decides
 * both the heading copy and where the fallback link actually goes: a
 * brand-new person has no password yet, so their fallback is choosing one
 * for the first time (the existing set-password flow reused from
 * reset-password). Someone who already has an account might not have a
 * password at all -- they may be SSO-only, especially after tonight's own
 * SSO work -- so their fallback deliberately doesn't name a mechanism
 * ("sign in a different way instead") rather than presuming one; it lands
 * on plain /auth/login, which itself still offers SSO alongside password.
 */

import { memo } from 'react';
import { Link } from 'react-router-dom';

import AuthShell from '../../components/auth-shell';
import shell from '../../components/auth-shell/index.module.css';
import SsoButtons from '../../components/sso-buttons';
import { OAuthProvider } from '../../../../fsm/auth';

export interface ActivateAccountProps {
  isLoading: boolean;
  customerName: string;
  /** Whether the invited email already has an Avrio account under a
   * different customer. Decides the subtitle and the fallback link's
   * destination and wording -- see the module comment. */
  accountExists: boolean;
  /** Where the fallback link should go. Computed by the caller, which
   * knows the invite token the reset-password flow needs. */
  passwordFallbackHref: string;
  onOAuthLogin: (provider: OAuthProvider) => void;
}

export const ActivateAccount = ({
  isLoading,
  customerName,
  accountExists,
  passwordFallbackHref,
  onOAuthLogin,
}: ActivateAccountProps) => (
  <AuthShell
    title={`You're invited to review candidates for ${customerName}`}
    subtitle={
      accountExists
        ? `You already have an Avrio account — this adds access for ${customerName}.`
        : undefined
    }
    footer={
      <Link to={passwordFallbackHref} className={shell.link}>
        {accountExists
          ? 'Sign in a different way instead'
          : 'Prefer a password instead?'}
      </Link>
    }
  >
    <SsoButtons
      disabled={isLoading}
      onSelect={onOAuthLogin}
      showDivider={false}
    />
  </AuthShell>
);

export default memo(ActivateAccount);
